import * as React from 'react';
import { UseQueryResult } from 'react-query';
import { CollapsibleToggle } from '@uifabric/portal-ux/lib/Collapsible/CollapsibleToggle';
import { ErrorLine } from '@uifabric/portal-ux/lib/ErrorLine';
import { LoadingLine } from '@uifabric/portal-ux/lib/LoadingLine';
import { ResponsiveMode } from '@fluentui/react/lib/utilities/decorators/withResponsiveMode';
import { useLocalization } from '@microsoft/portal-app/lib/shared/localization';
import { useCallback } from 'react';

import { ITermsOfUseCardAcceptanceProps, IAgreement } from '../models/types';
import { TermsOfUseCardCollapsed } from './TermsOfUseCardCollapsed';
import { TermsOfUseCardExpandedAuth } from './TermsOfUseCardExpandedAuth';
import { TermsOfUseCardExpandedUnauth } from './TermsOfUseCardExpandedUnauth';
import { LocalizedCollapsible } from '../../shared/LocalizedCollapsible';
import { classNames } from './TermsOfUseCard.styles';

export interface ITermsOfUseCardProps extends ITermsOfUseCardAcceptanceProps {
  // onExpand is only used when calling from ConsentPageContent.tsx
  readonly onExpand?: () => void;
  readonly getAgreementQuery: UseQueryResult<IAgreement, unknown>;
  readonly isAnonymousPage?: boolean;
}

export const TermsOfUseCard: React.FC<ITermsOfUseCardProps> = ({
  onExpand,
  agreementId,
  policyFileId,
  getAgreementQuery,
  isAnonymousPage,
  acceptanceDate,
  deviceDisplayName,
}) => {
  const [t] = useLocalization('TermsOfUse');
  const acceptanceDateText = acceptanceDate ? t('acceptedOn', { date: acceptanceDate }) : '';
  const deviceDisplayNameText =
    deviceDisplayName && deviceDisplayName.length > 0 ? t('acceptedOnDevice', { deviceName: deviceDisplayName }) : '';

  const agreementDisplayName =
    // Find the policy file being accepted and show its display name
    getAgreementQuery.data?.policyFiles?.find((policyFile) => policyFile.id === policyFileId)?.displayName ??
    // If not found, find the policy file being set as default and show its display name
    getAgreementQuery.data?.policyFiles?.find((policyFile) => policyFile.isDefault === true)?.displayName ??
    '';

  const ariaLabelText = agreementDisplayName + ', ' + acceptanceDateText + ', ' + deviceDisplayNameText + ',';

  const onRenderHeader = useCallback(() => {
    if (getAgreementQuery.isLoading) {
      return <LoadingLine />;
    } else if (getAgreementQuery.isError) {
      return <ErrorLine>{t('messageTermsOfUseNotLoaded')}</ErrorLine>;
    } else {
      return (
        <TermsOfUseCardCollapsed
          agreementDisplayName={agreementDisplayName}
          acceptanceDateText={acceptanceDateText}
          deviceDisplayNameText={deviceDisplayNameText}
        />
      );
    }
  }, [
    getAgreementQuery.isLoading,
    getAgreementQuery.isError,
    t,
    agreementDisplayName,
    acceptanceDateText,
    deviceDisplayNameText,
  ]);

  const onRenderContent = useCallback(() => {
    const agreementIdentifiers = { agreementId, policyFileId };
    return isAnonymousPage ? (
      <TermsOfUseCardExpandedUnauth {...agreementIdentifiers} />
    ) : (
      <TermsOfUseCardExpandedAuth {...agreementIdentifiers} />
    );
  }, [agreementId, policyFileId, isAnonymousPage]);

  return (
    <LocalizedCollapsible
      name={ariaLabelText}
      headerResponsiveMode={ResponsiveMode.medium}
      expanded={false}
      styles={{ root: classNames.panelHeadingCollapsedClass }}
      toggle={CollapsibleToggle.header}
      onRenderHeader={onRenderHeader}
      onRenderContent={onRenderContent}
      onExpand={onExpand}
    />
  );
};
